import React, { useContext } from "react";
import { FaCheckCircle, FaLightbulb, FaHandsHelping, FaBullhorn } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import "../TrainingGlobal.css";
import BannerImage from "./iso.jpg";

const ISO9001: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);

  const handleBooking = () => {
    if (isAuthenticated) {
      navigate('/booking/iso-9001');
    } else {
      navigate('/login', { state: { from: '/booking/iso-9001' } });
    }
  };

  return (
    <div className="training-page">
      <div className="training-max-width">

        {/* --- HERO --- */}
        <div className="training-hero" style={{ backgroundImage: `url(${BannerImage})` }}>
          <span className="hero-tag">ISO Training</span>
          <h1 className="hero-title">ISO 9001 Auditor</h1>
          <p className="hero-subtitle">
            Ensuring quality management and compliance
          </p>
          <button onClick={handleBooking} className="btn-hero">
            Book Now <span style={{ fontSize: '1.2em' }}>→</span>
          </button>
        </div>

        {/* --- CONTENT --- */}
        <div className="detail-content">

          <div className="content-article">
            <div className="article-section">
              <h2 className="article-heading">Introduction to ISO 9001:2015</h2>
              <p className="article-text">
                ISO 9001:2015 is an international standard for quality management systems (QMS). It provides a framework for organizations to consistently deliver products and services that meet customer and regulatory requirements. The standard emphasizes customer satisfaction, process efficiency, and continuous improvement. By implementing ISO 9001, organizations demonstrate their commitment to quality and gain a competitive edge in the market.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Key Principles of ISO 9001:2015</h2>
              <p className="article-text">
                The key principles include customer focus, leadership, engagement of people, process approach, improvement, evidence-based decision-making, and relationship management. Customer focus ensures that customer needs and expectations are met consistently. Leadership fosters a culture of quality and accountability. Engaging people empowers employees to contribute to quality objectives. These principles form the foundation of an effective QMS.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Quality Management System</h2>
              <p className="article-text">
                A Quality Management System (QMS) is a set of policies, processes, and procedures designed to ensure consistent quality in products and services. It provides a structured approach to managing quality across all organizational functions. A well-implemented QMS enhances operational efficiency, reduces waste, and improves customer satisfaction. Regular audits and reviews ensure the QMS remains effective and aligned with organizational goals.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Quality PDCA Cycle</h2>
              <p className="article-text">
                The PDCA (Plan-Do-Check-Act) cycle is a continuous improvement model used in quality management. It involves planning quality objectives, implementing processes, monitoring performance, and taking corrective actions. For example, a manufacturing company may use the PDCA cycle to reduce defects by analyzing root causes and implementing process improvements. This iterative approach ensures ongoing enhancement of quality and efficiency.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Structure of ISO 9001:2015 and Clauses</h2>
              <p className="article-text">
                ISO 9001:2015 follows the High-Level Structure (HLS), making it compatible with other ISO standards. The clauses include context of the organization, leadership, planning, support, operation, performance evaluation, and improvement. This structure ensures a systematic approach to quality management. Organizations can integrate ISO 9001 with other standards, such as ISO 14001, for a unified management system.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Internal and External Factors</h2>
              <p className="article-text">
                Internal factors, such as organizational culture and resources, and external factors, such as market trends and regulatory requirements, influence the implementation of a QMS. Understanding these factors helps organizations align their quality objectives with business goals. For example, analyzing customer feedback (an external factor) can guide product improvements, while leveraging employee expertise (an internal factor) enhances process efficiency.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Turtle Chart</h2>
              <p className="article-text">
                A turtle chart is a visual tool used to analyze processes by identifying inputs, outputs, resources, and controls. It provides a clear understanding of process interactions and dependencies. For example, a turtle chart for a production process may include raw materials as inputs, finished goods as outputs, machinery as resources, and quality checks as controls. This tool helps in optimizing processes and ensuring consistent quality.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Risk-based Thinking</h2>
              <p className="article-text">
                Risk-based thinking involves identifying and addressing risks and opportunities to ensure the QMS achieves its intended outcomes. For example, a company may identify supply chain disruptions as a risk and implement contingency plans to mitigate its impact. This proactive approach enhances resilience and ensures consistent quality. Risk-based thinking is integral to ISO 9001:2015 and supports continuous improvement.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Process Mapping</h2>
              <p className="article-text">
                Process mapping visually represents workflows, helping organizations identify inefficiencies and improve processes. For example, mapping the order fulfillment process can reveal bottlenecks and guide improvements to reduce lead times. Process mapping enhances transparency, aligns processes with quality objectives, and supports decision-making. It is a valuable tool for achieving operational excellence.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">ISO Documentation</h2>
              <p className="article-text">
                ISO documentation includes policies, procedures, work instructions, and records. It ensures consistency, compliance, and effective communication within the organization. For example, a documented procedure for handling customer complaints ensures timely resolution and continuous improvement. Maintaining accurate and up-to-date documentation supports audits and fosters a culture of accountability.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Implementation of ISO 9001:2015</h2>
              <p className="article-text">
                Implementing ISO 9001:2015 involves understanding requirements, conducting a gap analysis, training employees, and establishing processes to meet the standard. For example, a company may identify gaps in its quality control processes and implement additional checks to address them. Effective implementation enhances customer satisfaction, operational efficiency, and compliance with regulatory requirements.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Certification Process</h2>
              <p className="article-text">
                The certification process involves an external audit to verify compliance with ISO 9001:2015 requirements. Achieving certification demonstrates an organization's commitment to quality and continuous improvement. The process includes a gap analysis, implementation of corrective actions, and a final audit by a certification body. Certification enhances credibility, improves stakeholder confidence, and provides a competitive advantage in the market.
              </p>
            </div>
          </div>

          {/* --- SIDEBAR --- */}
          <div className="content-sidebar">
            <div className="sidebar-widget">
              <h4 className="widget-title">Key Elements</h4>
              <ul className="key-list">
                <li><FaCheckCircle className="check-icon" /> Quality Management</li>
                <li><FaLightbulb className="check-icon" /> Process Mapping</li>
                <li><FaHandsHelping className="check-icon" /> Risk-based Thinking</li>
                <li><FaBullhorn className="check-icon" /> Continuous Improvement</li>
              </ul>
            </div>

            <div className="sidebar-widget">
              <h4 className="widget-title">Why ISO 9001?</h4>
              <p style={{ color: '#aaa', lineHeight: '1.6', fontSize: '0.9rem' }}>
                Gain a competitive advantage with improved processes and increased customer satisfaction.
              </p>
            </div>

            <div className="sidebar-widget" style={{ background: 'linear-gradient(135deg, #111 0%, #222 100%)', border: '1px solid #333' }}>
              <h4 className="widget-title">Get Certified</h4>
              <p style={{ color: '#ccc', marginBottom: '20px', fontSize: '0.9rem' }}>
                Start your journey towards standard compliance.
              </p>

              <button
                onClick={handleBooking}
                className="btn-primary"
                style={{ width: '100%', justifyContent: 'center' }}
              >
                Book Now
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ISO9001;

